import { useCallback } from "react";
import { usePutApiSelflearnUpdateTopicSummaryProgress } from "@oneapp/data";
import type { FlashCardUI } from "../models/FlashcardUI";

type UseFlashcardEffectsParams = {
  examId: number;
  gradeId: number;
  subjectId: number;
  chapterId: number;
  chapterName: string;
  topicId: number;
  topicName: string;
};

export const useFlashcardEffects = ({
  examId,
  gradeId,
  subjectId,
  chapterId,
  chapterName,
  topicId,
  topicName,
}: UseFlashcardEffectsParams) => {
  const { mutate } = usePutApiSelflearnUpdateTopicSummaryProgress({
    request: { clientName: "selflearn" },
  });

  const bookmark = useCallback(
    (
      card: FlashCardUI,
      newIsBookmarked: boolean,
      parentIsCompleted: boolean,
      parentIsBookmarked: boolean,
    ) => {
      mutate({
        data: {
          examId,
          gradeId,
          subjectId,
          chapterId,
          chapterName,
          topicId,
          topicName,
          iremember: false,
          isBookmarked: parentIsBookmarked,
          isCompleted: parentIsCompleted,
          memoryCardId: null,
          flashCards: [
            {
              flashCardId: card.flashcardId,
              flashCardName: card.title,
              flashCardTypeId: card.typeId ?? 1,
              subTopicId: 0,
              isCompleted: card.isCompleted,
              isBookmarked: newIsBookmarked,
              timeTakenInSecs: 0,
            },
          ],
        },
      });
    },
    [
      mutate,
      examId,
      gradeId,
      subjectId,
      chapterId,
      chapterName,
      topicId,
      topicName,
    ],
  );

  const like = useCallback(
    (
      card: FlashCardUI,
      newIsLiked: boolean,
      newIsDisLiked: boolean,
      parentIsCompleted: boolean,
      parentIsBookmarked: boolean,
    ) => {
      mutate({
        data: {
          examId,
          gradeId,
          subjectId,
          chapterId,
          chapterName,
          topicId,
          topicName,
          iremember: false,
          isBookmarked: parentIsBookmarked,
          isCompleted: parentIsCompleted,
          memoryCardId: null,
          flashCards: [
            {
              flashCardId: card.flashcardId,
              flashCardName: card.title,
              flashCardTypeId: card.typeId ?? 1,
              subTopicId: 0,
              isCompleted: card.isCompleted,
              isBookmarked: card.isBookmarkSelected,
              isLiked: newIsLiked,
              isDisLiked: newIsDisLiked,
            },
          ],
        },
      });
    },
    [
      mutate,
      examId,
      gradeId,
      subjectId,
      chapterId,
      chapterName,
      topicId,
      topicName,
    ],
  );

  const saveProgress = useCallback(
    (
      card: FlashCardUI,
      timeTakenInSecs: number,
      parentIsCompleted: boolean,
      parentIsBookmarked: boolean,
    ) => {
      mutate({
        data: {
          examId,
          gradeId,
          subjectId,
          chapterId,
          chapterName,
          topicId,
          topicName,
          iremember: false,
          isBookmarked: parentIsBookmarked,
          isCompleted: parentIsCompleted,
          memoryCardId: null,
          flashCards: [
            {
              flashCardId: card.flashcardId,
              flashCardName: card.title,
              flashCardTypeId: card.typeId ?? 1,
              subTopicId: 0,
              isCompleted: true,
              isBookmarked: card.isBookmarkSelected,
              timeTakenInSecs,
            },
          ],
        },
      });
    },
    [
      mutate,
      examId,
      gradeId,
      subjectId,
      chapterId,
      chapterName,
      topicId,
      topicName,
    ],
  );

  return { bookmark, like, saveProgress };
};
